import React, { useMemo } from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { typography } from '../constants/typography';
import { useAppColors } from '../context/PreferencesContext';
import type { AttendancePunchSource } from '../types/attendance';
import {
  attendanceSourceCaption,
  resolveAttendanceSourceDisplay,
} from '../utils/attendanceEventSource';
import { formatAppliedOn, formatAttendanceTimestamp } from '../utils/dateTime';

type Props = {
  punch: AttendancePunchSource;
  treatAsManualEdit?: boolean;
  editedTime?: string | null;
  align?: 'left' | 'right';
};

function KioskGlyph({ color }: { color: string }) {
  return (
    <View style={styles.glyph} accessibilityElementsHidden>
      <View style={[styles.kioskScreen, { borderColor: color }]} />
      <View style={[styles.kioskBase, { backgroundColor: color }]} />
    </View>
  );
}

function MobileGlyph({ color }: { color: string }) {
  return (
    <View style={styles.glyph} accessibilityElementsHidden>
      <View style={[styles.phone, { borderColor: color }]}>
        <View style={[styles.phoneSpeaker, { backgroundColor: color }]} />
      </View>
    </View>
  );
}

function SourceGlyph({
  kind,
  color,
}: {
  kind: 'KIOSK' | 'MOBILE' | 'MANUAL' | 'OTHER';
  color: string;
}) {
  if (kind === 'KIOSK') return <KioskGlyph color={color} />;
  if (kind === 'MOBILE') return <MobileGlyph color={color} />;
  if (kind === 'MANUAL') {
    return (
      <Text style={[styles.hand, { color }]} accessibilityElementsHidden>
        ✋
      </Text>
    );
  }
  return null;
}

function tooltipLines(
  punch: AttendancePunchSource,
  editedTime: string | null | undefined,
  originalLabel: string | null,
): Array<{ label: string; value: string }> {
  const lines: Array<{ label: string; value: string }> = [
    { label: 'Status', value: 'Edited manually' },
  ];
  if (originalLabel) {
    lines.push({ label: 'Original source', value: originalLabel });
  }
  const rawTime = editedTime?.trim();
  const time =
    rawTime && rawTime !== 'Not available'
      ? /am|pm/i.test(rawTime)
        ? rawTime
        : formatAttendanceTimestamp(rawTime, rawTime)
      : null;
  if (time && time !== 'Not available') {
    lines.push({ label: 'Edited time', value: time });
  }
  if (punch.editedByName) {
    lines.push({ label: 'Edited by', value: punch.editedByName });
  }
  if (punch.editedAt) {
    const at = formatAppliedOn(punch.editedAt);
    if (at !== 'Not available') {
      lines.push({ label: 'Edited at', value: at });
    }
  }
  if (punch.editReason) {
    lines.push({ label: 'Reason', value: punch.editReason });
  }
  return lines;
}

export function AttendanceEventSourceBadge({
  punch,
  treatAsManualEdit = false,
  editedTime,
  align = 'left',
}: Props) {
  const palette = useAppColors();
  const display = resolveAttendanceSourceDisplay(punch, { treatAsManualEdit });
  const caption = attendanceSourceCaption(display);
  const themed = useMemo(
    () => ({
      caption: { color: palette.textSecondary },
    }),
    [palette],
  );

  if (!caption) return null;

  const lines = display.showManualEdit
    ? tooltipLines(display, editedTime, display.originalLabel)
    : [];
  const nativeTitle = lines
    .map(line => `${line.label}: ${line.value}`)
    .join('\n');
  const accessibilityLabel = display.showManualEdit
    ? `Manual Edit. ${nativeTitle}`
    : caption;

  return (
    <View
      accessible
      accessibilityLabel={accessibilityLabel}
      style={[
        styles.badge,
        align === 'right' ? styles.badgeRight : null,
      ]}
      {...(Platform.OS === 'web' && nativeTitle ? { title: nativeTitle } : null)}
    >
      <SourceGlyph kind={display.kind} color={palette.textSecondary} />
      <Text style={[styles.caption, themed.caption]}>{caption}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  badge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
    alignSelf: 'flex-start',
  },
  badgeRight: { alignSelf: 'flex-end', justifyContent: 'flex-end' },
  caption: { ...typography.caption, flexShrink: 1 },
  glyph: {
    width: 12,
    height: 14,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  kioskScreen: {
    width: 10,
    height: 8,
    borderWidth: 1.25,
    borderRadius: 1,
  },
  kioskBase: {
    width: 12,
    height: 2,
    marginTop: 1,
    borderRadius: 1,
  },
  phone: {
    width: 8,
    height: 12,
    borderWidth: 1.25,
    borderRadius: 2,
    alignItems: 'center',
    paddingTop: 1.5,
  },
  phoneSpeaker: {
    width: 3,
    height: 1,
    borderRadius: 1,
  },
  hand: {
    fontSize: 12,
    lineHeight: 14,
  },
});
